<template>
  <fieldset
    v-if="context.nodeData.value[level]"
    :class="['vehicle-selector-section', {'vehicle-selector-section--root': isRootNode}]"
    tabindex="0">
    <template v-if="!isRootNode">
      <legend :class="['h3-bold', 'vehicle-selector-section__legend', {'sr-only': hideLegend}]">
        {{ context.nodeData.value[level].node.channelNodeChoiceHelpText }}
      </legend>
      <oie-vehicle-selector-function-info
        v-if="context.nodeData.value[level].node.function"
        :function-data="context.nodeData.value[level].node.function"
        :level="level"></oie-vehicle-selector-function-info>
      <oie-base-input-v2
        v-if="context.nodeData.value[level].children.length > 10"
        v-model="search"
        :placeholder="tStatic('genericSearch')"
        class="vehicle-selector-section__search">
        <template #iconPrepend>
          <oie-base-icon icon="filter-icon" size="s"></oie-base-icon>
        </template>
      </oie-base-input-v2>
    </template>
    <ul
      ref="vehicleSelectorSectionElement"
      class="vehicle-selector-section__selections"
      aria-live="polite"
      :style="selectionsGridStyle">
      <li
        v-for="node in filteredData"
        :key="node.code">
        <oie-vehicle-selector-action
          :is-root-node="isRootNode"
          :is-link="useLinks && node.isModel"
          :context="context"
          :level="level"
          :node="node"
          @click="$emit('select', node)"></oie-vehicle-selector-action>
      </li>
      <li v-if="isRootNode">
        <oie-base-action
          v-if="hasOthers && !showOthers"
          theme="outlined-black"
          size="large"
          class="vehicle-selector-section__show-more"
          @click="showOthers = true">
          <template #iconPrepend>
            <oie-base-icon icon="add"></oie-base-icon>
          </template>
          {{ tStatic('showMoreLink') }}
        </oie-base-action>
      </li>
    </ul>
    <template v-if="!isRootNode">
      <oie-base-action
        v-if="hasOthers && !showOthers"
        class="vehicle-selector-section__show-more"
        theme="outlined-black"
        size="large"
        @click="showOthers = true">
        <template #iconPrepend>
          <oie-base-icon icon="add"></oie-base-icon>
        </template>
        {{ tStatic('showMoreLink') }}
      </oie-base-action>
    </template>
    <oie-vehicle-selector-add-node
      v-if="showAddNode"
      :context="context"
      :level="level"></oie-vehicle-selector-add-node>
    <div
      v-if="shouldShowAfterListInfo"
      class="vehicle-selector__extended-info text-formatter">
      <h2 class="vehicle-selector__extended-info-heading" v-html="context.nodeData.value[level].node.channelNodeExtendedInformationHeading"></h2>
      <p class="vehicle-selector__extended-info-text" v-html="context.nodeData.value[level].node.channelNodeExtendedInformation"></p>
    </div>
  </fieldset>
</template>

<script>
import { computed, onMounted, defineComponent, ref } from '@vue/composition-api'
import _ from 'lodash'
import useFilteredNodeData from '@/composables/useFilteredNodeData'
import useElementSize from '@/composables/useElementSize'

export default defineComponent({
  props: {
    level: {
      type: String,
      default: '0'
    },
    context: {
      type: Object,
      required: true
    },
    useLinks: {
      type: Boolean,
      default: false
    },
    hideOthers: {
      type: Boolean,
      default: false
    },
    hideLegend: {
      type: Boolean,
      default: false
    },
    showAddNode: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const isRootNode = computed(() => props.level === '0')
    const { search, showOthers, hasOthers, filteredData } = useFilteredNodeData(computed(() => { return props.context.nodeData.value[props.level] }), props.hideOthers)
    const vehicleSelectorSectionElement = ref(null)
    const { width: containerWidth } = useElementSize(vehicleSelectorSectionElement)
    const selectionsGridStyle = computed(() => {
      if (filteredData.value) {
        let columns = 4
        if(containerWidth.value < 640) {
          columns = 1
        } else if (containerWidth.value < 767) {
          columns = 2
        }
        const rows = Math.ceil(filteredData.value.length / columns)
        return `grid-template-columns: repeat(${columns}, 1fr); grid-template-rows: repeat(${rows}, 1fr);`
      }
    })

    const isLastLevel = computed(() => {
      return props.level === props.context.lastLevel.value
    })

    const hasExtendedInfo = computed(() => {
      const info = _.get(props.context.nodeData.value, [props.level, 'node', 'channelNodeExtendedInformation'], '')
      return info.trim().length > 0
    })

    const shouldShowAfterListInfo = computed(() => {
      return !isRootNode.value && isLastLevel.value && hasExtendedInfo.value
    })

    onMounted(() => {
      if(props.hideOthers) {
        const children = _.get(props.context.nodeData.value, [props.level, 'children'], [])
        showOthers.value = _.some(children, child => props.context.isSelected(child.code) && child.isOther)
      }
    })
    return {
      search,
      isRootNode,
      hasOthers,
      showOthers,
      filteredData,
      shouldShowAfterListInfo,
      vehicleSelectorSectionElement,
      containerWidth,
      selectionsGridStyle
    }
  }
})
</script>

<style lang="scss">
.vehicle-selector-section {
  display: flex;
  flex-direction: column;
  gap: measure('half');
  container-type: inline-size;
}

.vehicle-selector-section__legend {
  margin-bottom: measure(quarter);
}

.vehicle-selector-section__search {
  max-width: 20rem;
}

.vehicle-selector-section__selections {
  @include reset-list;
  gap: measure('quarter');
  display: grid;
  grid-auto-flow: column;
}

.vehicle-selector-section__selections:not(.vehicle-selector-section--root) > li {
  margin-bottom: measure(quarter);
  break-inside: avoid;
}

/*------------------------------------*\
   Styles for root node section in wide context (i.e desktop)
\*------------------------------------*/
.vehicle-selector-section--root .vehicle-selector-section__selections {
  @container (min-width: 767px) {
    columns: unset;
    display: flex;
    flex-wrap: wrap;
  }
}

.vehicle-selector-section__show-more {
  align-self: flex-start;

  @container (max-width: 639px) {
    width: 100%;
  }
}

.vehicle-selector-section__legend-row {
  display: flex;
  align-items: center;
  gap: measure('half');
  margin-bottom: measure(quarter);
}

.vehicle-selector-section__info-button {
  @include reset-button;
  display: flex;
  align-items: center;
  gap: measure('eighth');
  color: color('primary');
  font-weight: weight('bold');
  cursor: pointer;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }

  &:focus-visible {
    outline: 2px solid color('primary');
    outline-offset: 2px;
  }
}

.vehicle-selector-section__extended-info-panel {
  background-color: color('background-blue-4');
  border: 1px solid color('border');
  border-radius: measure('eighth');
  padding: measure('half');
  margin-bottom: measure('quarter');
}

.vehicle-selector-section__extended-info-static {
  background-color: color('background-blue-4');
  border: 1px solid color('border');
  border-radius: measure('eighth');
  padding: measure('half');
}
</style>
